import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const productNavItems = [
  { path: '/markets', label: 'Markets' },
  { path: '/ai-agents', label: 'AI Agents' },
  { path: '/vaults', label: 'Vaults' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/fees', label: 'Fees' },
];

const markets = [
  { pair: 'BTC-PERP', price: 45230.50, change24h: 2.34, volume24h: 1245000000, funding: 0.0123, high: 46100.00, low: 44120.00 },
  { pair: 'ETH-PERP', price: 3124.80, change24h: -1.12, volume24h: 856000000, funding: 0.0087, high: 3180.00, low: 3050.00 },
  { pair: 'SOL-PERP', price: 142.30, change24h: 5.67, volume24h: 423000000, funding: 0.0156, high: 148.00, low: 134.00 },
  { pair: 'ARB-PERP', price: 0.89, change24h: -0.45, volume24h: 123000000, funding: 0.0034, high: 0.92, low: 0.87 },
  { pair: 'DOGE-PERP', price: 0.12, change24h: 8.90, volume24h: 567000000, funding: 0.0210, high: 0.13, low: 0.11 },
  { pair: 'LINK-PERP', price: 14.56, change24h: 1.23, volume24h: 89000000, funding: 0.0056, high: 15.00, low: 14.10 },
  { pair: 'AVAX-PERP', price: 35.20, change24h: -2.10, volume24h: 234000000, funding: 0.0098, high: 36.50, low: 34.00 },
  { pair: 'OP-PERP', price: 2.34, change24h: 3.45, volume24h: 78000000, funding: 0.0045, high: 2.45, low: 2.22 },
  { pair: 'SUI-PERP', price: 1.45, change24h: -5.60, volume24h: 156000000, funding: 0.0180, high: 1.55, low: 1.38 },
  { pair: 'SEI-PERP', price: 0.56, change24h: 12.30, volume24h: 89000000, funding: 0.0250, high: 0.60, low: 0.49 },
  { pair: 'APT-PERP', price: 8.90, change24h: -0.80, volume24h: 112000000, funding: 0.0067, high: 9.20, low: 8.60 },
  { pair: 'NEAR-PERP', price: 4.56, change24h: 4.20, volume24h: 67000000, funding: 0.0110, high: 4.80, low: 4.30 },
];

function formatNumber(n: number) {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toFixed(2)}`;
}

export default function MarketsPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('markets.title', 'Markets')}
      subtitle={t('markets.subtitle', 'Trade perpetual futures on 50+ cryptocurrency pairs with up to 50x leverage')}
      category="Product"
      navItems={productNavItems}
    >
      <div className={`rounded-xl border ${borderLight} ${bgCard} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${borderLight} ${textMuted}`}>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.pair', 'Pair')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.price', 'Price')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.change24h', '24h Change')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.volume24h', '24h Volume')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.funding', 'Funding')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.high24h', '24h High')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.low24h', '24h Low')}</th>
              </tr>
            </thead>
            <tbody>
              {markets.map((m) => (
                <tr key={m.pair} className={`border-b ${borderLight} last:border-0 hover:bg-[#00ff88]/5 transition-colors cursor-pointer`}>
                  <td className={`px-4 py-3 font-medium ${textPrimary}`}>{m.pair}</td>
                  <td className={`px-4 py-3 text-right font-mono ${textPrimary}`}>{m.price.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-mono ${m.change24h >= 0 ? 'text-[#00ff88]' : 'text-[#ff5566]'}`}>
                    {m.change24h >= 0 ? '+' : ''}{m.change24h}%
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{formatNumber(m.volume24h)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{(m.funding * 100).toFixed(2)}%</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{m.high.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{m.low.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CompanyLayout>
  );
}